import { CameraIcon, DocumentTextIcon, UserIcon } from '@heroicons/react/24/outline';
import { Button } from '../../components/Button';

interface ProfileIdentityCardProps {
  username: string;
  description: string;
  onUsernameChange: (value: string) => void;
  onDescriptionChange: (value: string) => void;
  onSave: () => void;
  isSaving: boolean;
  imageUrl: string;
  isUploadingImage: boolean;
  onImageClick: () => void;
  fileInputRef: React.RefObject<HTMLInputElement>;
  onFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

/**
 * Presentational component for profile identity editing.
 * Shows profile picture with upload, username and description form.
 */
export const ProfileIdentityCard = ({
  username,
  description,
  onUsernameChange,
  onDescriptionChange,
  onSave,
  isSaving,
  imageUrl,
  isUploadingImage,
  onImageClick,
  fileInputRef,
  onFileChange,
}: ProfileIdentityCardProps) => {
  return (
    <div className="bg-surface rounded-3xl shadow-xl overflow-hidden border border-subtle transition-all duration-300 hover:shadow-2xl">
      <div className="px-6 pt-8 pb-8">
        {/* Profile Picture Container */}
        <div className="relative mb-6 flex justify-center">
          <div className="relative group">
            <div className="h-32 w-32 rounded-full ring-4 ring-white shadow-lg overflow-hidden bg-surface">
              <img
                src={imageUrl}
                alt={username}
                className="h-full w-full object-cover"
                onError={(e) => (e.currentTarget.src = '/default-profile-picture.jpg')}
              />
            </div>

            {/* Camera Button Overlay */}
            <Button
              type="button"
              variant="primary"
              size="sm"
              onClick={onImageClick}
              disabled={isUploadingImage}
              className="absolute bottom-1 right-1 p-2 rounded-full group-hover:scale-110 duration-200"
              title="Change photo"
            >
              {isUploadingImage ? (
                <div className="h-5 w-5 border-2 border-surface border-t-transparent rounded-full animate-spin" />
              ) : (
                <CameraIcon className="h-5 w-5" />
              )}
            </Button>
            <input
              type="file"
              ref={fileInputRef}
              className="hidden"
              accept="image/*"
              onChange={onFileChange}
            />
          </div>
        </div>

        {/* Identity Form */}
        <div className="space-y-6">
          <div className="text-center">
            <h2 className="text-xl  text-main flex items-center justify-center gap-2">
              {username}
            </h2>
          </div>

          <div className="bg-subtle rounded-xl p-4 border border-subtle space-y-4">
            <div>
              <label
                htmlFor="username"
                className="block text-xs  text-muted uppercase tracking-wider mb-2"
              >
                Passport Name (Username)
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <UserIcon className="h-5 w-5 text-disabled" aria-hidden="true" />
                </div>
                <input
                  type="text"
                  id="username"
                  className="block w-full pl-10 pr-3 py-2 border border-interactive rounded-lg focus:ring-brand focus:border-brand sm:text-sm transition-shadow shadow-sm"
                  placeholder="Your username"
                  value={username}
                  onChange={(e) => onUsernameChange(e.target.value)}
                />
              </div>
            </div>

            <div>
              <label
                htmlFor="description"
                className="block text-xs  text-muted uppercase tracking-wider mb-2"
              >
                About Me
              </label>
              <div className="relative">
                <div className="absolute top-3 left-3 flex items-start pointer-events-none">
                  <DocumentTextIcon className="h-5 w-5 text-disabled" aria-hidden="true" />
                </div>
                <textarea
                  id="description"
                  rows={3}
                  className="block w-full pl-10 pr-3 py-2 border border-interactive rounded-lg focus:ring-brand focus:border-brand sm:text-sm transition-shadow shadow-sm"
                  placeholder="Tell us about your travels..."
                  value={description}
                  onChange={(e) => onDescriptionChange(e.target.value)}
                />
              </div>
            </div>
          </div>

          <Button
            type="button"
            variant="primary"
            fullWidth
            onClick={onSave}
            disabled={isSaving}
            loading={isSaving}
          >
            {isSaving ? 'Updating Passport...' : 'Save Passport Details'}
          </Button>
        </div>
      </div>
    </div>
  );
};
